/**
 * What a route shows instead of its screen: not found, failed, on its way.
 *
 * These are the router's defaults (`app/router.tsx`), so they are what ANY
 * address can end in — a mistyped public URL as much as a deleted strategy —
 * and they are in the entry chunk for that reason. Hence where this file
 * lives and what it may import: the kit's button, icon and skeleton, each
 * from its own file, and nothing from a signed-in feature
 * (`test/route-splitting.test.ts` holds that line).
 *
 * INSIDE THE FRAME OR NOT. An address under `/app` that matches nothing is
 * drawn by the router inside the shell's outlet, with the rail and the bar
 * still there; `/nope` is drawn alone. The shell says which through
 * `InShellContext`, and the state lays itself out as a page or as the whole
 * viewport accordingly. A screen that fails does the same: the frame stays,
 * so the way out — the rail, search, Sign out — is never lost to an error.
 *
 * AN ERROR IS AMBER, with a glyph and a sentence (DECISION rule 1). It says
 * what did not happen to the user's work ("Nothing you built was changed"),
 * shows the server's own message when there is one, and offers Try again.
 * A refusal is not routed here: a screen shows its own, where it happened.
 */
import { Link, useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { OctagonX } from "lucide-react";
import { createContext, useContext, useEffect, useRef } from "react";
import { ApiError } from "../app/api-client.js";
import { Button } from "../components/kit/Button.js";
import { Icon } from "../components/kit/Icon.js";
import { Skeleton, SkeletonGroup } from "../components/kit/Skeleton.js";
import "./route-states.css";

/** True for everything the signed-in shell renders in its outlet. */
export const InShellContext = createContext(false);

/** The half-lit disc. A copy of the shell's mark: this file may not import a signed-in feature. */
function Mark() {
  return (
    <svg viewBox="0 0 24 24" width="1.375rem" height="1.375rem" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 2.8a9.2 9.2 0 0 0 0 18.4z" fill="currentColor" />
    </svg>
  );
}

/** Move focus to the state's heading: the page changed, and this is its title. */
function useHeadingFocus() {
  const ref = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, []);
  return ref;
}

export function RouteNotFound() {
  const inShell = useContext(InShellContext);
  const heading = useHeadingFocus();
  return (
    <main className="route-state" data-frame={inShell ? "shell" : "page"} data-testid="route-not-found">
      {!inShell && (
        <Link to="/" className="route-state__mark">
          <Mark />
          Madaline
        </Link>
      )}
      <p className="route-state__eyebrow">Not found</p>
      <h1 ref={heading} tabIndex={-1} className="route-state__title">
        That page does not exist
      </h1>
      <p className="route-state__text">
        The address may be mistyped, or what it pointed to — a strategy, a run, a model — may have been
        deleted. Nothing was changed.
      </p>
      <div className="route-state__actions">
        {inShell ? (
          <>
            <Button asChild variant="primary">
              <Link to="/app">Go to the Overview</Link>
            </Button>
            <Button asChild>
              <Link to="/app/strategies">Strategies</Link>
            </Button>
            <Button asChild>
              <Link to="/app/runs">Runs</Link>
            </Button>
          </>
        ) : (
          <>
            <Button asChild variant="primary">
              <Link to="/">Back to the start</Link>
            </Button>
            <Button asChild>
              <Link to="/docs">Documentation</Link>
            </Button>
          </>
        )}
      </div>
    </main>
  );
}

/** The server's own words, when the failure came with any. */
function detailOf(error: unknown): string | null {
  if (error instanceof ApiError) {
    if (error.isNetwork) return "Madaline could not be reached. Check your connection.";
    return `${error.code} · ${error.message}`;
  }
  if (error instanceof Error && error.message) return error.message;
  return null;
}

export function RouteError({ error, reset }: ErrorComponentProps) {
  const inShell = useContext(InShellContext);
  const router = useRouter();
  const heading = useHeadingFocus();
  const detail = detailOf(error);

  const retry = (): void => {
    // Re-run the route's guards and loaders, then let the boundary try again.
    void router.invalidate().finally(() => reset());
  };

  return (
    <main className="route-state" data-frame={inShell ? "shell" : "page"} data-testid="route-error">
      {!inShell && (
        <Link to="/" className="route-state__mark">
          <Mark />
          Madaline
        </Link>
      )}
      <p className="route-state__eyebrow" data-tone="attention">
        <Icon as={OctagonX} size="sm" />
        Something went wrong
      </p>
      <h1 ref={heading} tabIndex={-1} className="route-state__title">
        This screen could not be shown
      </h1>
      <p className="route-state__text" role="alert">
        It failed while it was loading. Nothing you built was changed, and anything that is running is
        still running.
      </p>
      {detail !== null && <p className="route-state__detail">{detail}</p>}
      <div className="route-state__actions">
        <Button variant="primary" onClick={retry}>
          Try again
        </Button>
        <Button asChild>{inShell ? <Link to="/app">Go to the Overview</Link> : <Link to="/">Back to the start</Link>}</Button>
      </div>
    </main>
  );
}

/**
 * A route that is taking a while: the shape of a page, after the router's own
 * one-second grace (the bar's progress line has been up since 200ms). A fast
 * navigation never sees this.
 */
export function RoutePending() {
  return (
    <SkeletonGroup label="Loading the page" immediate className="route-pending" data-testid="route-pending">
      <Skeleton width="14rem" height="1.75rem" />
      <Skeleton width="26rem" style={{ maxWidth: "70%" }} />
      <div className="route-pending__rows">
        <Skeleton width="92%" />
        <Skeleton width="78%" />
        <Skeleton width="85%" />
        <Skeleton width="64%" />
      </div>
    </SkeletonGroup>
  );
}
